'use client';

/**
 * Zone authoring state, owned by the COP page rather than by the map stage.
 *
 * It moved here in slice 8, when the zones panel became the right column's
 * fourth tab. The panel and the map now sit in different subtrees - the panel is
 * inside `CopSidePanel`, the drawn shape is painted by `DroneMap` - and both
 * have to read one set of points. `page.tsx` renders both, so it holds this and
 * hands each side what it needs; a second copy on either side is how a half-
 * drawn polygon ends up on screen and not in the thing that saves it.
 *
 * The COP is the only place a geofence can be authored anywhere in the product:
 * `(dashboard)/geofences/page.tsx` is a bare `redirect('/')`.
 */

/**
 * The GEOMETRY of the zone being drawn. Whether the map is armed for drawing,
 * and as which shape, is not here any more — that is the COP's one map mode
 * (`lib/map-mode.ts`), because it is what a click has to arbitrate against
 * every other armed interaction. A second copy of it here is exactly how two
 * modes used to end up armed at once.
 */
export interface ZoneState {
  showPanel: boolean;
  drawPoints: [number, number][];
  polygonClosed: boolean;
  circleCenter: { lat: number; lng: number } | null;
  circleRadius: number;
}

export type ZoneAction =
  | { type: 'TOGGLE_PANEL' }
  | { type: 'CLOSE_PANEL' }
  /** The shape changed (or drawing stopped) — start the geometry over. */
  | { type: 'RESET_DRAWING' }
  | { type: 'MAP_CLICK'; lat: number; lng: number; shape: 'circle' | 'polygon' | null }
  | { type: 'CLOSE_POLYGON' }
  | { type: 'SET_CIRCLE_CENTER'; lat: number; lng: number }
  | { type: 'SET_CIRCLE_RADIUS'; radius: number };

export const initialZoneState: ZoneState = {
  showPanel: false,
  drawPoints: [],
  polygonClosed: false,
  circleCenter: null,
  circleRadius: 500,
};

const DRAW_RESET = {
  drawPoints: [] as [number, number][],
  polygonClosed: false,
  circleCenter: null,
  circleRadius: 500,
} satisfies Partial<ZoneState>;

export function zoneReducer(state: ZoneState, action: ZoneAction): ZoneState {
  switch (action.type) {
    case 'TOGGLE_PANEL':
      return { ...state, showPanel: !state.showPanel };
    case 'CLOSE_PANEL':
      return { ...state, ...DRAW_RESET, showPanel: false };
    case 'RESET_DRAWING':
      return { ...state, ...DRAW_RESET };
    case 'MAP_CLICK':
      if (action.shape === 'circle') {
        return { ...state, circleCenter: { lat: action.lat, lng: action.lng } };
      }
      if (action.shape === 'polygon' && !state.polygonClosed) {
        return { ...state, drawPoints: [...state.drawPoints, [action.lng, action.lat]] };
      }
      return state;
    case 'CLOSE_POLYGON':
      return state.drawPoints.length >= 3 ? { ...state, polygonClosed: true } : state;
    case 'SET_CIRCLE_CENTER':
      return { ...state, circleCenter: { lat: action.lat, lng: action.lng } };
    case 'SET_CIRCLE_RADIUS':
      return { ...state, circleRadius: action.radius };
    default:
      return state;
  }
}
